/**
 * As fórmulas do caixa: saldos, fechamento do mês, tabelas, gráfico e ordem.
 *
 * Papel: aqui ficam o mês-base, o saldo inicial efetivo, o fechamento do mês, as
 * três tabelas por categoria, o gráfico trimestral, o Saldo Atual a ordem dos
 * lançamentos dentro do mês, a listagem da aba Lançamentos com o saldo corrido e o rodapé, a soma da barra de lote e as contas do modal e do perfil do favorecido. A API as roda sobre o caixa inteiro lido do banco,
 * e a reconciliação as confere contra a exportação de 25/09.
 *
 * Como usar: **toda soma é em centavos inteiros** e só vira reais na saída. Somar
 * reais em ponto flutuante faria o total de um mês sair um centavo diferente da
 * soma das linhas que a tela mostra; quem acrescentar uma soma aqui deverá passar
 * por `centavosDe` e devolver por `emReais`.
 *
 * O lado do caixa vem do tipo, por `entraNoCaixa`. Os quatro tipos entram nos
 * KPIs, no saldo e no Saldo Atual; as tabelas de Saídas e de Entradas leem só
 * despesa e só receita, e a movimentação tem o cartão próprio.
 *
 * O saldo gravado de um mês **depois do mês-base nunca é lido**: o saldo inicial
 * efetivo dele é o do mês-base mais os pagos de todos os meses até o anterior.
 * Um saldo digitado num mês posterior não muda número nenhum da tela.
 *
 * A linha da origem de cada regra está em `docs/modulos/financas.md`, "Regras de
 * negócio".
 */
import { arredondarCentavos } from './formatos';
import {
  type AjudaCustoDoFavorecido,
  CATEGORIA_CREDITO_MOVIMENTACAO,
  CATEGORIA_DEBITO_MOVIMENTACAO,
  type FiltroDeLancamentos,
  type FormaPagamentoLancamento,
  type Repeticao,
  type StatusLancamento,
  type TabelaDoFechamento,
  type TipoLancamento,
  type Trimestres,
  datasDasParcelas,
  listaTodosOsMeses,
  valorDoLancamento,
} from './financas';

/** O mínimo de um lançamento que as fórmulas leem. `Lancamento` cabe aqui. */
export interface LancamentoDoCaixa {
  id: string;
  data: string;
  tipo: TipoLancamento;
  categoria: string;
  subcategoria: string | null;
  descricao: string;
  formaPagamento: FormaPagamentoLancamento | null;
  favorecido: { nome: string } | null;
  valorBruto: number;
  desconto: number;
  acrescimo: number;
  pago: boolean;
  provisao: boolean;
  posicao: number;
}

/** Um mês gravado: o saldo digitado e a marca de ordem manual. */
export interface MesDoCaixa {
  competencia: string;
  saldoInicial: number;
  ordemManual: boolean;
}

/**
 * O caixa inteiro de uma casa: todos os meses gravados e todos os lançamentos.
 *
 * As fórmulas precisam do caixa inteiro, e não de um mês: o saldo de um mês
 * depende de todos os anteriores desde o mês-base.
 */
export interface CaixaDoCalculo<L extends LancamentoDoCaixa = LancamentoDoCaixa> {
  meses: readonly MesDoCaixa[];
  lancamentos: readonly L[];
}

/**
 * Diz se o tipo entra no caixa.
 *
 * @param tipo - O tipo do lançamento.
 * @returns Verdadeiro para receita e crédito de movimentação; falso para despesa
 *   e débito de movimentação.
 */
export function entraNoCaixa(tipo: TipoLancamento): boolean {
  return tipo === 'receita' || tipo === 'credito_movimentacao';
}

/**
 * O valor de um lançamento em centavos inteiros.
 *
 * @param lancamento - Bruto, desconto e acréscimo, em reais.
 * @returns `valorDoLancamento` arredondado ao centavo e multiplicado por cem.
 */
export function centavosDe(lancamento: {
  valorBruto: number;
  desconto: number;
  acrescimo: number;
}): number {
  return Math.round(arredondarCentavos(valorDoLancamento(lancamento)) * 100);
}

/** Centavos de volta a reais, na saída de toda soma. */
function emReais(centavos: number): number {
  return centavos / 100;
}

/** O valor com o sinal do lado do caixa: positivo quando entra. */
function assinado(lancamento: LancamentoDoCaixa): number {
  return entraNoCaixa(lancamento.tipo) ? centavosDe(lancamento) : -centavosDe(lancamento);
}

/**
 * A competência de uma data.
 *
 * @param data - `AAAA-MM-DD`.
 * @returns `AAAA-MM`.
 */
export function competenciaDe(data: string): string {
  return data.slice(0, 7);
}

/**
 * A situação de um lançamento, como o selo da tabela a escreve.
 *
 * @param lancamento - Pago e provisão.
 * @returns `pago` quando pago, mesmo com provisão; senão `provisionado` com
 *   provisão e `lancado` sem ela.
 */
export function statusDoLancamento(lancamento: {
  pago: boolean;
  provisao: boolean;
}): StatusLancamento {
  if (lancamento.pago) return 'pago';
  return lancamento.provisao ? 'provisionado' : 'lancado';
}

/** O saldo gravado de cada competência, em centavos. */
function saldosGravados(caixa: CaixaDoCalculo): Map<string, number> {
  return new Map(
    caixa.meses.map((mes) => [
      mes.competencia,
      Math.round(arredondarCentavos(mes.saldoInicial) * 100),
    ]),
  );
}

/**
 * O mês-base: a menor competência com saldo gravado diferente de zero ou com
 * algum lançamento.
 *
 * @param caixa - O caixa inteiro.
 * @returns A competência, ou `null` no caixa sem saldo e sem lançamento.
 */
export function mesBase(caixa: CaixaDoCalculo): string | null {
  const comLancamento = new Set(caixa.lancamentos.map((l) => competenciaDe(l.data)));
  const gravados = saldosGravados(caixa);
  const candidatas = [...new Set([...gravados.keys(), ...comLancamento])].sort();
  return (
    candidatas.find(
      (competencia) =>
        (gravados.get(competencia) ?? 0) !== 0 || comLancamento.has(competencia),
    ) ?? null
  );
}

/**
 * O saldo com que um mês começa.
 *
 * Até o mês-base, inclusive, é o saldo gravado do próprio mês, ou zero. Depois
 * dele, é o saldo gravado do mês-base mais os pagos que entram e menos os pagos
 * que saem de todos os meses do mês-base ao anterior ao pedido.
 *
 * @param caixa - O caixa inteiro.
 * @param competencia - `AAAA-MM`.
 * @returns O saldo inicial, em reais.
 */
export function saldoInicialEfetivo(caixa: CaixaDoCalculo, competencia: string): number {
  const gravados = saldosGravados(caixa);
  const base = mesBase(caixa);
  if (base === null || competencia <= base) return emReais(gravados.get(competencia) ?? 0);

  let centavos = gravados.get(base) ?? 0;
  for (const lancamento of caixa.lancamentos) {
    const dele = competenciaDe(lancamento.data);
    if (lancamento.pago && dele >= base && dele < competencia)
      centavos += assinado(lancamento);
  }
  return emReais(centavos);
}

/** Os quatro KPIs de um mês. */
export interface FechamentoDoMes {
  saldoInicial: number;
  entradas: number;
  saidas: number;
  saldoFinal: number;
}

/**
 * Os KPIs de um mês: entradas e saídas pagas e o saldo final.
 *
 * @param caixa - O caixa inteiro.
 * @param competencia - `AAAA-MM`.
 * @returns Saldo inicial efetivo, entradas, saídas e saldo final; os pendentes
 *   ficam fora.
 */
export function fechamentoDoMes(caixa: CaixaDoCalculo, competencia: string): FechamentoDoMes {
  const saldoInicial = saldoInicialEfetivo(caixa, competencia);
  let entradas = 0;
  let saidas = 0;
  for (const lancamento of caixa.lancamentos) {
    if (!lancamento.pago || competenciaDe(lancamento.data) !== competencia) continue;
    if (entraNoCaixa(lancamento.tipo)) entradas += centavosDe(lancamento);
    else saidas += centavosDe(lancamento);
  }
  return {
    saldoInicial,
    entradas: emReais(entradas),
    saidas: emReais(saidas),
    saldoFinal: emReais(Math.round(saldoInicial * 100) + entradas - saidas),
  };
}

/**
 * As competências que têm ordem manual.
 *
 * @param caixa - O caixa inteiro.
 * @returns As competências marcadas por um arraste.
 */
export function mesesComOrdemManual(caixa: CaixaDoCalculo): Set<string> {
  return new Set(caixa.meses.filter((mes) => mes.ordemManual).map((mes) => mes.competencia));
}

/**
 * Ordena lançamentos na ordem da tabela.
 *
 * Entre meses, pela competência. No mês com ordem manual, pela `posicao`; nos
 * outros, pela data e, no mesmo dia, pela `posicao`. O identificador só desempata
 * duas linhas de mesma posição, o que não acontece em mês regravado pela API.
 *
 * @param lancamentos - Em qualquer ordem; o array não é alterado.
 * @param manuais - As competências com ordem manual, de `mesesComOrdemManual`.
 * @returns Uma cópia ordenada.
 */
export function ordenarLancamentos<L extends LancamentoDoCaixa>(
  lancamentos: readonly L[],
  manuais: ReadonlySet<string>,
): L[] {
  return [...lancamentos].sort((a, b) => {
    const mesA = competenciaDe(a.data);
    const mesB = competenciaDe(b.data);
    if (mesA !== mesB) return mesA < mesB ? -1 : 1;
    if (manuais.has(mesA) && a.posicao !== b.posicao) return a.posicao - b.posicao;
    if (a.data !== b.data) return a.data < b.data ? -1 : 1;
    if (a.posicao !== b.posicao) return a.posicao - b.posicao;
    if (a.id === b.id) return 0;
    return a.id < b.id ? -1 : 1;
  });
}

/** Uma subcategoria sendo somada. */
interface SubcategoriaEmSoma {
  centavos: number;
  transacoes: LancamentoDoCaixa[];
}

/** Uma categoria sendo somada, com as subcategorias na ordem em que apareceram. */
interface CategoriaEmSoma {
  centavos: number;
  subcategorias: Map<string, SubcategoriaEmSoma>;
}

/**
 * Agrega linhas numa tabela por categoria e subcategoria.
 *
 * As categorias saem por valor decrescente; no empate, na ordem em que
 * apareceram nas linhas, que chegam na ordem do mês. As subcategorias, idem. Uma
 * linha só vira transação da subcategoria quando a categoria e a subcategoria
 * **gravadas** são os dois rótulos: a linha de categoria vazia (somada em
 * "Outros") ou de subcategoria vazia (somada em "—" ou na descrição) entra no
 * valor e não aparece ao abrir a subcategoria.
 *
 * @param linhas - As linhas pagas da tabela, na ordem do mês.
 * @param rotulos - O nome de categoria e de subcategoria sob o qual cada linha soma.
 * @param cores - A cor gravada de cada categoria do plano.
 * @returns A tabela, com os valores em reais.
 */
function montarTabela(
  linhas: readonly LancamentoDoCaixa[],
  rotulos: (linha: LancamentoDoCaixa) => { categoria: string; subcategoria: string },
  cores: ReadonlyMap<string, string | null>,
): TabelaDoFechamento {
  const categorias = new Map<string, CategoriaEmSoma>();
  let total = 0;

  for (const linha of linhas) {
    const { categoria, subcategoria } = rotulos(linha);
    const centavos = centavosDe(linha);
    total += centavos;

    const daCategoria = categorias.get(categoria) ?? { centavos: 0, subcategorias: new Map() };
    daCategoria.centavos += centavos;
    const daSubcategoria = daCategoria.subcategorias.get(subcategoria) ?? {
      centavos: 0,
      transacoes: [],
    };
    daSubcategoria.centavos += centavos;
    if (linha.categoria === categoria && linha.subcategoria === subcategoria) {
      daSubcategoria.transacoes.push(linha);
    }
    daCategoria.subcategorias.set(subcategoria, daSubcategoria);
    categorias.set(categoria, daCategoria);
  }

  return {
    total: emReais(total),
    categorias: [...categorias.entries()]
      .sort(([, a], [, b]) => b.centavos - a.centavos)
      .map(([nome, daCategoria]) => ({
        nome,
        cor: cores.get(nome) ?? null,
        valor: emReais(daCategoria.centavos),
        subcategorias: [...daCategoria.subcategorias.entries()]
          .sort(([, a], [, b]) => b.centavos - a.centavos)
          .map(([nomeDaSubcategoria, daSubcategoria]) => ({
            nome: nomeDaSubcategoria,
            valor: emReais(daSubcategoria.centavos),
            lancamentos: [...daSubcategoria.transacoes]
              .sort((a, b) => (a.data === b.data ? 0 : a.data < b.data ? -1 : 1))
              .map((transacao) => ({
                id: transacao.id,
                data: transacao.data,
                descricao: transacao.descricao,
                formaPagamento: transacao.formaPagamento,
                valor: emReais(centavosDe(transacao)),
                status: statusDoLancamento(transacao),
              })),
          })),
      })),
  };
}

/** As três tabelas por categoria do Fechamento. */
export interface TabelasDoFechamento {
  saidasPorCategoria: TabelaDoFechamento;
  entradasPorCategoria: TabelaDoFechamento;
  /** Nula no mês sem crédito nem débito de movimentação pago. */
  movimentacao: TabelaDoFechamento | null;
}

/**
 * As tabelas de Saídas, Entradas e Movimentação de um mês.
 *
 * Saídas lê só despesa paga, com "Outros" e "—" para os vazios; Entradas, só
 * receita paga, com "Entradas" para a categoria vazia e a descrição — ou "—" —
 * para a subcategoria vazia; Movimentação, os créditos pagos e depois os débitos
 * pagos, com a categoria pela natureza quando vazia.
 *
 * @param caixa - O caixa inteiro.
 * @param competencia - `AAAA-MM`.
 * @param cores - A cor gravada de cada categoria; a categoria fora do mapa sai
 *   com `cor: null`.
 * @returns As três tabelas.
 */
export function tabelasDoFechamento(
  caixa: CaixaDoCalculo,
  competencia: string,
  cores: ReadonlyMap<string, string | null>,
): TabelasDoFechamento {
  const pagosDoMes = ordenarLancamentos(
    caixa.lancamentos.filter((l) => l.pago && competenciaDe(l.data) === competencia),
    mesesComOrdemManual(caixa),
  );
  const creditos = pagosDoMes.filter((l) => l.tipo === 'credito_movimentacao');
  const debitos = pagosDoMes.filter((l) => l.tipo === 'debito_movimentacao');

  return {
    saidasPorCategoria: montarTabela(
      pagosDoMes.filter((l) => l.tipo === 'despesa'),
      (l) => ({ categoria: l.categoria || 'Outros', subcategoria: l.subcategoria || '—' }),
      cores,
    ),
    entradasPorCategoria: montarTabela(
      pagosDoMes.filter((l) => l.tipo === 'receita'),
      (l) => ({
        categoria: l.categoria || 'Entradas',
        subcategoria: l.subcategoria || l.descricao || '—',
      }),
      cores,
    ),
    movimentacao:
      creditos.length === 0 && debitos.length === 0
        ? null
        : montarTabela(
            [...creditos, ...debitos],
            (l) => ({
              categoria:
                l.categoria ||
                (l.tipo === 'credito_movimentacao'
                  ? CATEGORIA_CREDITO_MOVIMENTACAO
                  : CATEGORIA_DEBITO_MOVIMENTACAO),
              subcategoria: l.subcategoria || '—',
            }),
            cores,
          ),
  };
}

/** Os rótulos dos trimestres, do primeiro ao quarto. */
const ROTULOS_DOS_TRIMESTRES = ['1º Tri', '2º Tri', '3º Tri', '4º Tri'];

/** Os meses abreviados, como a dica do gráfico os escreve. */
const MESES_ABREVIADOS = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
];

/** A soma das posições `inicio..fim` de um array de doze meses. */
function somaDosMeses(valores: readonly number[], inicio: number, fim: number): number {
  let soma = 0;
  for (let mes = inicio; mes <= fim; mes += 1) soma += valores[mes] ?? 0;
  return soma;
}

/**
 * O gráfico trimestral do ano do mês aberto.
 *
 * Soma os pagos que saem — despesa **e** débito de movimentação — por categoria,
 * de janeiro ao mês aberto. Há um trimestre por rótulo até o do mês aberto, e o
 * corrente só vai até o mês aberto. As séries saem pelo total no ano, decrescente.
 *
 * @param caixa - O caixa inteiro.
 * @param competencia - O mês aberto, `AAAA-MM`.
 * @param cores - A cor gravada de cada categoria.
 * @returns Os rótulos, os meses de cada trimestre, os totais e as séries.
 */
export function trimestresDoAno(
  caixa: CaixaDoCalculo,
  competencia: string,
  cores: ReadonlyMap<string, string | null>,
): Trimestres {
  const ano = Number(competencia.slice(0, 4));
  const mesAberto = Number(competencia.slice(5, 7)) - 1;
  const porCategoria = new Map<string, number[]>();
  const porMes = Array.from({ length: 12 }, () => 0);

  for (const lancamento of ordenarLancamentos(caixa.lancamentos, mesesComOrdemManual(caixa))) {
    const mes = Number(lancamento.data.slice(5, 7)) - 1;
    if (!lancamento.pago || entraNoCaixa(lancamento.tipo)) continue;
    if (Number(lancamento.data.slice(0, 4)) !== ano || mes > mesAberto) continue;

    const categoria = lancamento.categoria || 'Sem categoria';
    const valores = porCategoria.get(categoria) ?? Array.from({ length: 12 }, () => 0);
    valores[mes] = (valores[mes] ?? 0) + centavosDe(lancamento);
    porCategoria.set(categoria, valores);
    porMes[mes] = (porMes[mes] ?? 0) + centavosDe(lancamento);
  }

  const intervalos: [number, number][] = [];
  for (let trimestre = 0; trimestre <= Math.floor(mesAberto / 3); trimestre += 1) {
    const inicio = trimestre * 3;
    intervalos.push([inicio, Math.min(inicio + 2, mesAberto)]);
  }

  return {
    ano,
    rotulos: intervalos.map((_, trimestre) => ROTULOS_DOS_TRIMESTRES[trimestre] ?? ''),
    mesesPorTrimestre: intervalos.map(([inicio, fim]) =>
      MESES_ABREVIADOS.slice(inicio, fim + 1),
    ),
    totais: intervalos.map(([inicio, fim]) => emReais(somaDosMeses(porMes, inicio, fim))),
    series: [...porCategoria.entries()]
      .map(([categoria, valores]) => ({
        categoria,
        valores,
        total: somaDosMeses(valores, 0, 11),
      }))
      .sort((a, b) => b.total - a.total)
      .map((serie) => ({
        categoria: serie.categoria,
        cor: cores.get(serie.categoria) ?? null,
        valores: intervalos.map(([inicio, fim]) =>
          emReais(somaDosMeses(serie.valores, inicio, fim)),
        ),
      })),
  };
}

/**
 * O Saldo Atual: o saldo gravado do mês-base mais todos os pagos desde ele.
 *
 * Não depende de filtro nem de "hoje": o pago de data futura já conta.
 *
 * @param caixa - O caixa inteiro.
 * @returns O saldo, em reais; zero no caixa sem mês-base.
 */
export function saldoAtual(caixa: CaixaDoCalculo): number {
  const base = mesBase(caixa);
  if (base === null) return 0;

  let centavos = saldosGravados(caixa).get(base) ?? 0;
  for (const lancamento of caixa.lancamentos) {
    if (lancamento.pago && competenciaDe(lancamento.data) >= base)
      centavos += assinado(lancamento);
  }
  return emReais(centavos);
}

/**
 * O saldo corrido de cada lançamento pago.
 *
 * É o saldo inicial efetivo do mês da linha mais os pagos do mês até ela,
 * inclusive, na ordem do mês — o mesmo número em qualquer filtro, porque a soma
 * corre sobre o mês inteiro e não sobre o que o filtro mostra.
 *
 * @param caixa - O caixa inteiro.
 * @returns O saldo por identificador; o pendente não tem entrada.
 */
export function saldosCorridos(caixa: CaixaDoCalculo): Map<string, number> {
  const saldos = new Map<string, number>();
  let competenciaAtual: string | null = null;
  let centavos = 0;

  for (const lancamento of ordenarLancamentos(caixa.lancamentos, mesesComOrdemManual(caixa))) {
    const competencia = competenciaDe(lancamento.data);
    if (competencia !== competenciaAtual) {
      competenciaAtual = competencia;
      centavos = Math.round(saldoInicialEfetivo(caixa, competencia) * 100);
    }
    if (!lancamento.pago) continue;
    centavos += assinado(lancamento);
    saldos.set(lancamento.id, emReais(centavos));
  }

  return saldos;
}

/** Os filtros da aba Lançamentos, sem a casa. */
export type FiltroDaListagem = Omit<FiltroDeLancamentos, 'residenciaId'>;

/**
 * Aplica os filtros da aba Lançamentos sobre a fonte.
 *
 * Tipo, categoria e forma por igualdade; status `pago` ou `pendente`; pesquisa
 * contida, sem diferenciar maiúsculas, em "favorecido descrição categoria
 * subcategoria"; "de" e "até" inclusivos. O período invertido não deixa linha
 * nenhuma.
 *
 * @param lancamentos - A fonte: o mês aberto ou todos os meses.
 * @param filtro - Os filtros; `competencia` não é lida aqui.
 * @returns As linhas que passam, na ordem em que vieram.
 */
export function filtrarLancamentos<L extends LancamentoDoCaixa>(
  lancamentos: readonly L[],
  filtro: FiltroDaListagem,
): L[] {
  const pesquisa = (filtro.pesquisa ?? '').toLowerCase();

  return lancamentos.filter((lancamento) => {
    if (filtro.tipo !== undefined && lancamento.tipo !== filtro.tipo) return false;
    if (filtro.categoria !== undefined && lancamento.categoria !== filtro.categoria)
      return false;
    if (filtro.status === 'pago' && !lancamento.pago) return false;
    if (filtro.status === 'pendente' && lancamento.pago) return false;
    if (filtro.forma !== undefined && lancamento.formaPagamento !== filtro.forma) return false;
    if (pesquisa !== '') {
      const texto = `${lancamento.favorecido?.nome ?? ''} ${lancamento.descricao} ${lancamento.categoria} ${lancamento.subcategoria ?? ''}`;
      if (!texto.toLowerCase().includes(pesquisa)) return false;
    }
    if (filtro.de !== undefined && lancamento.data < filtro.de) return false;
    if (filtro.ate !== undefined && lancamento.data > filtro.ate) return false;
    return true;
  });
}

/**
 * O líquido de um recorte: o que entra menos o que sai, pendentes inclusive.
 *
 * @param lancamentos - As linhas que o filtro mostra.
 * @returns O rodapé da tabela, em reais.
 */
export function somaDoRecorte(lancamentos: readonly LancamentoDoCaixa[]): number {
  return emReais(lancamentos.reduce((soma, lancamento) => soma + assinado(lancamento), 0));
}

/**
 * A listagem da aba Lançamentos.
 *
 * A fonte é todos os meses com pesquisa não vazia ou com período, e o mês de
 * `filtro.competencia` nos outros casos. Os filtros correm sobre a fonte, as
 * linhas saem na ordem da tabela e cada pago leva o saldo corrido.
 *
 * @param caixa - O caixa inteiro.
 * @param filtro - Os filtros da tela.
 * @returns As linhas, com `saldoCorrido` nulo nos pendentes, e o rodapé.
 */
export function listarLancamentos<L extends LancamentoDoCaixa>(
  caixa: CaixaDoCalculo<L>,
  filtro: FiltroDaListagem,
): { lancamentos: (L & { saldoCorrido: number | null })[]; soma: number } {
  const fonte = listaTodosOsMeses(filtro)
    ? caixa.lancamentos
    : caixa.lancamentos.filter((l) => competenciaDe(l.data) === filtro.competencia);
  const filtrados = filtrarLancamentos(fonte, filtro);
  const saldos = saldosCorridos(caixa);

  return {
    lancamentos: ordenarLancamentos(filtrados, mesesComOrdemManual(caixa)).map(
      (lancamento) => ({
        ...lancamento,
        saldoCorrido: saldos.get(lancamento.id) ?? null,
      }),
    ),
    soma: somaDoRecorte(filtrados),
  };
}

/** A soma da barra de lote e qual dos três rótulos ela mostra. */
export interface SomaDaSelecao {
  entradas: number;
  saidas: number;
  liquido: number;
  /** `misto` com os dois lados; `entradas` só com entradas; `saidas` nos outros casos. */
  exibicao: 'misto' | 'entradas' | 'saidas';
}

/**
 * A soma das linhas selecionadas, pendentes inclusive.
 *
 * @param lancamentos - As linhas marcadas, como foram guardadas ao marcar.
 * @returns Entradas, saídas, líquido e o rótulo a mostrar.
 */
export function somaDaSelecao(
  lancamentos: readonly Pick<
    LancamentoDoCaixa,
    'tipo' | 'valorBruto' | 'desconto' | 'acrescimo'
  >[],
): SomaDaSelecao {
  let entradas = 0;
  let saidas = 0;
  for (const lancamento of lancamentos) {
    if (entraNoCaixa(lancamento.tipo)) entradas += centavosDe(lancamento);
    else saidas += centavosDe(lancamento);
  }

  let exibicao: SomaDaSelecao['exibicao'] = 'saidas';
  if (entradas > 0 && saidas > 0) exibicao = 'misto';
  else if (entradas > 0) exibicao = 'entradas';

  return {
    entradas: emReais(entradas),
    saidas: emReais(saidas),
    liquido: emReais(entradas - saidas),
    exibicao,
  };
}

/**
 * O "Valor Final" do modal.
 *
 * @param valorBruto - O valor digitado, em reais; zero quando vazio.
 * @param desconto - O desconto, em reais; zero quando vazio.
 * @param acrescimo - O acréscimo, em reais; zero quando vazio.
 * @returns `valorBruto − desconto + acrescimo` arredondado ao centavo, ou `null`
 *   quando não passa de zero — a tela mostra "—".
 */
export function valorFinalDoModal(
  valorBruto: number,
  desconto: number,
  acrescimo: number,
): number | null {
  const final = arredondarCentavos(valorBruto - desconto + acrescimo);
  return final > 0 ? final : null;
}

/**
 * Os totais da ajuda de custo no perfil do favorecido.
 *
 * @param ajuda - As quatro linhas; nula é desmarcada.
 * @returns O total de 30 dias de cada diária (nulo na desmarcada) e a soma das
 *   linhas marcadas, com auxílio e INSS pelo valor único.
 */
export function totaisDoPerfil(
  ajuda: Pick<
    AjudaCustoDoFavorecido,
    'custoDiaria' | 'diariasDiaria' | 'medicaValor' | 'inssValor'
  >,
): { custo: number | null; diarias: number | null; total: number } {
  const custo = ajuda.custoDiaria === null ? null : ajuda.custoDiaria * 30;
  const diarias = ajuda.diariasDiaria === null ? null : ajuda.diariasDiaria * 30;
  return {
    custo,
    diarias,
    total: (custo ?? 0) + (diarias ?? 0) + (ajuda.medicaValor ?? 0) + (ajuda.inssValor ?? 0),
  };
}

/**
 * As datas que uma gravação do modal ocupa, na ordem em que os lançamentos nascem.
 *
 * Sem repetição, só a data. Na recorrência, a data e mais um mês por cópia, com o
 * dia recuado ao último do mês. No parcelamento por intervalo, `datasDasParcelas`;
 * no avulso, as datas digitadas, na ordem em que foram digitadas.
 *
 * @param data - A data do modal, `AAAA-MM-DD`.
 * @param repeticao - O que as duas seções de baixo do modal pedem.
 * @returns Uma data por lançamento; a primeira é a do original ou da parcela 1.
 */
export function datasDaRepeticao(data: string, repeticao: Repeticao): string[] {
  if (repeticao === null) return [data];
  if (repeticao.tipo === 'recorrencia') {
    return datasDasParcelas(data, repeticao.meses + 1, 'mensal');
  }
  if (repeticao.intervalo === 'avulso') return [...repeticao.datas];
  return datasDasParcelas(data, repeticao.parcelas, repeticao.intervalo);
}

/**
 * O tipo que uma categoria padrão faz o modal escolher.
 *
 * @param categoria - O nome da categoria; vazio não infere nada.
 * @param plano - As categorias do plano com a natureza de cada uma.
 * @returns A natureza da categoria no plano, `despesa` para a que está fora dele
 *   e `null` para o nome vazio.
 */
export function tipoDaCategoria(
  categoria: string,
  plano: readonly { nome: string; natureza: TipoLancamento }[],
): TipoLancamento | null {
  if (categoria === '') return null;
  return plano.find((item) => item.nome === categoria)?.natureza ?? 'despesa';
}
